/**
 * The renderer host shared by the match and the hangar (Phase 6): backend probe and renderer
 * creation, the post-processing graph and its rebuilds on settings changes, the frame gate and
 * timing, frame statistics, dynamic resolution, GPU timing, and the drawing buffer following
 * its container. The owner supplies the scene and the camera, populates the scene, and does its
 * per-frame work inside the callback `begin` runs before each render.
 */
import type { Node } from 'three/webgpu';
import { Vector2, type PerspectiveCamera, type Scene, type WebGPURenderer } from 'three/webgpu';

import {
  matchesPreset,
  needsGraphRebuild,
  resolveEffects,
  type EffectConfig,
  type PresetName,
} from '../../settings/presets.js';
import type { Settings } from '../../settings/schema.js';
import { chooseBackend, probeWebGPU, type BackendName } from '../backend.js';
import type { UrlOptions } from '../urlParams.js';
import { budgetMs, drsConfig, drsStep, initialDrs, type DrsConfig, type DrsState } from './drs.js';
import { frameGate, type FrameGateState } from './framecap.js';
import { createGradeLut } from './lut.js';
import { buildPostGraph, type PostGraph } from './pipeline.js';
import { createRenderer, type RendererHandle } from './renderer.js';
import { FrameStats, type StatsSnapshot } from './stats.js';

export interface RenderHostOptions {
  canvas: HTMLCanvasElement;
  /** The element whose CSS size the drawing buffer follows. */
  container: HTMLElement;
  signal: AbortSignal;
  settings: Settings;
  url: UrlOptions;
  scene: Scene;
  camera: PerspectiveCamera;
  /** Narrows the resolved effects for this surface (the match never runs depth of field). */
  mask?: (config: EffectConfig) => EffectConfig;
  /** Depth-of-field focus distance as a node, when the surface moves it every frame. */
  dofFocus?: Node;
  /** The drawing buffer followed the container; the owner updates its camera's aspect. */
  onResize?: (width: number, height: number) => void;
  /** A frame was rendered (or skipped under `render=0`); `count` frames so far. */
  onFrame?: (count: number) => void;
}

export interface RenderSnapshot extends StatsSnapshot {
  backend: BackendName | null;
  gpuTiming: boolean;
  preset: PresetName | 'custom';
  drawingBuffer: { width: number; height: number };
}

/** What the frame overlay and the debug API read from any rendering surface. */
export interface RenderStatsSource {
  readonly ready: boolean;
  readonly error: string | null;
  readonly backend: BackendName | null;
  readonly gpuTiming: boolean;
  readonly frameCount: number;
  snapshot(): RenderSnapshot;
  resetStats(): void;
  quality(): EffectConfig;
}

export interface FrameTiming {
  /** Wall-clock milliseconds since the previous rendered frame (capped when read as `dt`). */
  frameMs: number;
  /** Seconds since the previous frame, capped at 0.25 s. */
  dt: number;
  /** Seconds since the page loaded. */
  now: number;
}

export interface SettingsChange {
  prev: EffectConfig;
  config: EffectConfig;
  anisotropyChanged: boolean;
}

export class RenderHost {
  backend: BackendName | null = null;
  gpuTiming = false;
  frameCount = 0;

  private settings: Settings;
  private config: EffectConfig;
  private handle: RendererHandle | null = null;
  private graph: PostGraph | null = null;
  private readonly lut = createGradeLut();
  private readonly stats = new FrameStats();
  private readonly bufferSize = new Vector2();
  private drs: DrsState;
  private drsCfg: DrsConfig;
  private gate: FrameGateState = { last: 0 };
  private lastTime: number | null = null;
  private lastGpuMs: number | null = null;
  private pendingGpu = false;
  private resizeObserver: ResizeObserver | null = null;
  private stopped = false;
  private disposed = false;

  constructor(private readonly opts: RenderHostOptions) {
    this.settings = opts.settings;
    this.config = this.resolve(opts.settings);
    this.drs = initialDrs(this.config.renderScale);
    this.drsCfg = drsConfig(budgetMs(this.config.fpsCap), this.config.renderScale);
  }

  /** The effects in force for this surface (after the mask). */
  get effects(): EffectConfig {
    return this.config;
  }

  get renderer(): WebGPURenderer | null {
    return this.handle?.renderer ?? null;
  }

  get maxAnisotropy(): number {
    return this.handle?.maxAnisotropy ?? 1;
  }

  /** Probes for WebGPU, picks the backend and creates the renderer. */
  async init(onBackend?: (backend: BackendName) => void): Promise<RendererHandle> {
    const { signal, canvas, url } = this.opts;
    const probe = await probeWebGPU();
    signal.throwIfAborted();
    const backend = chooseBackend({
      ...probe,
      forceWebGL: this.settings.forceWebGL,
      override: url.backend,
    });
    onBackend?.(backend);
    const handle = await createRenderer(canvas, backend === 'webgl2', signal);
    this.handle = handle;
    this.backend = handle.backend;
    this.gpuTiming = handle.gpuTiming;
    this.resize();
    return handle;
  }

  /**
   * Builds the post graph, follows the container's size and starts the animation loop. Call
   * once the scene holds everything the first frame needs.
   */
  begin(frame: (timing: FrameTiming) => void): void {
    const handle = this.handle;
    if (!handle) throw new Error('renderer not initialised');
    this.graph = this.buildGraph();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.opts.container);
    void handle.renderer.setAnimationLoop((time) => this.tick(time, frame));
  }

  /** Applies changed settings live: the graph is rebuilt only when an effect changed. */
  applySettings(next: Settings): SettingsChange {
    const prev = this.config;
    const config = this.resolve(next);
    this.settings = next;
    this.config = config;
    this.drsCfg = drsConfig(budgetMs(config.fpsCap), config.renderScale);
    if (
      config.dynamicResolution !== prev.dynamicResolution ||
      config.renderScale !== prev.renderScale
    ) {
      this.drs = initialDrs(config.renderScale);
    }
    if (!config.dynamicResolution) this.drs = { ...this.drs, scale: config.renderScale };
    if (this.graph) {
      if (needsGraphRebuild(prev, config)) {
        this.graph.dispose();
        this.graph = this.buildGraph();
      } else {
        this.graph.setScale(this.drs.scale);
      }
    }
    return { prev, config, anisotropyChanged: config.anisotropy !== prev.anisotropy };
  }

  resize(): void {
    if (!this.handle) return;
    const rect = this.opts.container.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    this.handle.resize(width, height, window.devicePixelRatio || 1);
    this.opts.onResize?.(width, height);
  }

  snapshot(): RenderSnapshot {
    const size = this.handle
      ? this.handle.renderer.getDrawingBufferSize(this.bufferSize)
      : this.bufferSize.set(0, 0);
    return {
      ...this.stats.snapshot(),
      backend: this.backend,
      gpuTiming: this.gpuTiming,
      preset: matchesPreset(this.settings),
      drawingBuffer: { width: size.x, height: size.y },
    };
  }

  resetStats(): void {
    this.stats.reset();
  }

  quality(): EffectConfig {
    return this.config;
  }

  /** Stops rendering without freeing anything (the route is about to change). */
  stop(): void {
    this.stopped = true;
    const renderer = this.handle?.renderer;
    if (renderer) void renderer.setAnimationLoop(null);
  }

  async dispose(): Promise<void> {
    this.disposed = true;
    this.stopped = true;
    this.resizeObserver?.disconnect();
    const renderer = this.handle?.renderer;
    if (renderer) void renderer.setAnimationLoop(null);
    this.graph?.dispose();
    this.graph = null;
    this.lut.dispose();
    if (renderer) {
      // The r186 runtime returns a promise although the 0.185 typings say void.
      await Promise.resolve(renderer.dispose() as unknown).catch(() => undefined);
    }
  }

  private resolve(settings: Settings): EffectConfig {
    const config = resolveEffects(settings);
    return this.opts.mask ? this.opts.mask(config) : config;
  }

  private buildGraph(): PostGraph {
    if (!this.handle) throw new Error('renderer not initialised');
    return buildPostGraph({
      renderer: this.handle.renderer,
      scene: this.opts.scene,
      camera: this.opts.camera,
      config: this.config,
      lut: this.lut,
      scale: this.drs.scale,
      ...(this.opts.dofFocus ? { dofFocus: this.opts.dofFocus } : {}),
    });
  }

  private tick(time: number, frame: (timing: FrameTiming) => void): void {
    const { handle, graph } = this;
    if (this.disposed || this.stopped || !handle || !graph) return;
    if (this.lastTime === null) {
      this.lastTime = time;
      return;
    }
    const gate = frameGate(this.gate, time, this.config.fpsCap);
    this.gate = { last: gate.last };
    if (!gate.render) return;
    const frameMs = time - this.lastTime;
    this.lastTime = time;
    frame({ frameMs, dt: Math.min(frameMs, 250) / 1000, now: time / 1000 });
    if (this.disposed || this.stopped) return;

    if (this.opts.url.render) {
      const t0 = performance.now();
      graph.pipeline.render();
      const cpuMs = performance.now() - t0;
      this.collectGpuTime();
      const info = handle.renderer.info.render;
      this.stats.push({
        frameMs,
        cpuMs,
        gpuMs: this.lastGpuMs,
        drawCalls: info.drawCalls,
        triangles: info.triangles,
        renderScale: this.drs.scale,
      });
      if (this.config.dynamicResolution) {
        const next = drsStep(this.drs, this.gpuTiming ? this.lastGpuMs : cpuMs, this.drsCfg);
        if (next.scale !== this.drs.scale) graph.setScale(next.scale);
        this.drs = next;
      }
    }
    this.frameCount++;
    this.opts.onFrame?.(this.frameCount);
  }

  private collectGpuTime(): void {
    if (!this.gpuTiming || this.pendingGpu || !this.handle) return;
    const renderer = this.handle.renderer;
    this.pendingGpu = true;
    void renderer
      .resolveTimestampsAsync()
      .then((ms) => {
        const value = typeof ms === 'number' ? ms : renderer.info.render.timestamp;
        if (Number.isFinite(value) && value > 0) this.lastGpuMs = value;
        this.pendingGpu = false;
      })
      .catch(() => {
        this.gpuTiming = false;
        this.pendingGpu = false;
      });
  }
}
